// Pure helpers for the patient feedback screen (NABH AAC.6.a). Kept free of
// React so they can be unit-tested directly.

export const COMPLAINT_CATEGORIES = [
  { value: "care", label: "Care & treatment" },
  { value: "staff", label: "Staff behaviour" },
  { value: "billing", label: "Billing" },
  { value: "facility", label: "Facility & cleanliness" },
  { value: "other", label: "Other" },
];

export const COMPLAINT_STATUS_LABELS = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

export const MIN_DESCRIPTION_LENGTH = 10;
export const MIN_SUBJECT_LENGTH = 3;
export const MAX_SUBJECT_LENGTH = 200;

export const statusLabel = (status, fallback) =>
  fallback || COMPLAINT_STATUS_LABELS[status] || status || "";

/** Newest visit first; tolerates missing/invalid dates. */
export const sortVisitsNewestFirst = (visits = []) =>
  [...visits].sort((a, b) => {
    const ta = new Date(a?.date || 0).getTime() || 0;
    const tb = new Date(b?.date || 0).getTime() || 0;
    return tb - ta;
  });

/** The patient's own score for a visit, from the overview `responses`. */
export const scoreForVisit = (responses = [], appointmentId) => {
  if (!appointmentId) return null;
  const hit = responses.find(
    (r) => String(r?.appointmentId || "") === String(appointmentId)
  );
  return hit && typeof hit.overall === "number" ? hit.overall : null;
};

/**
 * Build the `answers` array for POST /patient-feedback/me/responses from the
 * form state: `{ [key]: number }` for stars, `{ [key]: string }` for text.
 * Returns `{ answers, missing }` — `missing` is the first required question
 * that has no rating (the backend would reject it with "Please rate: …").
 */
export const buildAnswers = (questions = [], ratings = {}, texts = {}) => {
  const answers = [];
  let missing = null;
  for (const q of questions) {
    if (!q?.key) continue;
    if (q.type === "text") {
      const answer = String(texts[q.key] ?? "").trim();
      if (!answer && q.required && !missing) missing = q;
      answers.push({ key: q.key, answer });
    } else {
      const rating = ratings[q.key];
      const has = Number.isInteger(rating) && rating >= 1 && rating <= 5;
      if (!has) {
        // Locked NABH questions are always required; extras follow their flag.
        if ((q.required ?? true) && !missing) missing = q;
        continue;
      }
      answers.push({ key: q.key, rating });
    }
  }
  return { answers, missing };
};

/** Client-side mirror of the backend complaint rules (3–200 / 10–5000). */
export const validateComplaint = ({ target, category, subject, description }) => {
  if (!target) return "Select the clinic or visit the complaint is about";
  if (!COMPLAINT_CATEGORIES.some((c) => c.value === category))
    return "Select a category";
  const s = String(subject || "").trim();
  if (s.length < MIN_SUBJECT_LENGTH)
    return `Subject must be at least ${MIN_SUBJECT_LENGTH} characters`;
  if (s.length > MAX_SUBJECT_LENGTH)
    return `Subject must be at most ${MAX_SUBJECT_LENGTH} characters`;
  const d = String(description || "").trim();
  if (d.length < MIN_DESCRIPTION_LENGTH)
    return `Please describe the issue in at least ${MIN_DESCRIPTION_LENGTH} characters`;
  if (d.length > 5000) return "Description must be at most 5000 characters";
  return null;
};

/**
 * The complaint picker encodes its choice as "visit:<appointmentId>" or
 * "clinic:<clinicId>"; this turns it into the POST body fields.
 */
export const complaintTargetToBody = (target) => {
  if (!target) return {};
  const [kind, id] = String(target).split(":");
  if (kind === "visit" && id) return { appointmentId: id };
  if (kind === "clinic" && id) return { clinicId: id };
  return {};
};
